import type {Values} from '@wix/devzai-utils-common';
import type {BymoPageProps} from '../bymo-pages/bymo-page-props';
import type {DoppeDtoInnerPage} from '../types/doppe-dto-inner-page';
import {DoppeDtoInnerPageKnownIds} from '../types/doppe-dto-inner-page';

/**
 * Viewer page types - specific types for each page and vertical item
 */
export const DoppeViewerPageType = {
    /**
     * Home page (main page with no inner page selected)
     */
    Home: 'Home',
    
    /**
     * Custom content page (user-created inner page)
     */
    ContentPage: 'ContentPage',
    
    /**
     * Products list page (vertical list)
     */
    ProductsList: 'ProductsList',
    
    /**
     * Single product detail page (vertical item)
     */
    ProductItem: 'ProductItem',
    
    /**
     * Blog posts list page (vertical list)
     */
    BlogPostsList: 'BlogPostsList',
    
    /**
     * Single blog post detail page (vertical item)
     */
    BlogPostItem: 'BlogPostItem',
    
    /**
     * Events list page (vertical list)
     */
    EventsList: 'EventsList',
    
    /**
     * Single event detail page (vertical item)
     */
    EventItem: 'EventItem',
    
    /**
     * Services list page (vertical list - Wix Bookings)
     */
    ServicesList: 'ServicesList',
    
    /**
     * Single service detail page (vertical item)
     */
    ServiceItem: 'ServiceItem',
} as const;

export type DoppeViewerPageType = Values<typeof DoppeViewerPageType>;

/**
 * Viewer page info - discriminated union for type-safe page navigation
 * 
 * Each page type has specific properties:
 * - Home: No additional properties
 * - Content pages: pageId, slug
 * - List pages: pageId, slug
 * - Item pages: pageId, itemSlug (product/post/event/service)
 * 
 * @example
 * ```typescript
 * const homePageInfo: DoppeViewerPageInfo = {
 *     pageType: DoppeViewerPageType.Home
 * };
 * 
 * const productItemInfo: DoppeViewerPageInfo = {
 *     pageType: DoppeViewerPageType.ProductItem,
 *     pageId: 'products-page-id',
 *     productSlug: 'my-cool-product'
 * };
 * ```
 */
export type DoppeViewerPageInfo =
    | DoppeViewerPageInfo.Home
    | DoppeViewerPageInfo.ContentPage
    | DoppeViewerPageInfo.ProductsList
    | DoppeViewerPageInfo.ProductItem
    | DoppeViewerPageInfo.BlogPostsList
    | DoppeViewerPageInfo.BlogPostItem
    | DoppeViewerPageInfo.EventsList
    | DoppeViewerPageInfo.EventItem
    | DoppeViewerPageInfo.ServicesList
    | DoppeViewerPageInfo.ServiceItem
;

// TODO: expand metadata to contain SEO info, social share settings, etc.
export type DoppeViewerPageMetadata = {
    pageTitle: string;
}

export namespace DoppeViewerPageInfo {
    
    /**
     * Base interface for all page info types
     */
    interface Base<TYPE extends DoppeViewerPageType> {
        pageType: TYPE;
    }
    
    // ========================================================================
    // Home Page
    // ========================================================================
    
    /**
     * Home page (main page with no inner page selected)
     */
    export interface Home extends Base<typeof DoppeViewerPageType.Home> {
        // No additional properties
    }
    
    // ========================================================================
    // Content Pages
    // ========================================================================
    
    /**
     * Custom content page (user-created inner page)
     * 
     * Note: pageId can be calculated from slug using bymoPagePropsGetInnerPageBySlug()
     */
    export interface ContentPage extends Base<typeof DoppeViewerPageType.ContentPage> {
        /**
         * URL slug for the page
         */
        slug: string;
    }
    
    // ========================================================================
    // Products (Wix Stores)
    // ========================================================================
    
    /**
     * Products list page (shows all products)
     * Uses constant path: /products
     */
    export interface ProductsList extends Base<typeof DoppeViewerPageType.ProductsList> {
        // No pageId - uses constant path
    }
    
    /**
     * Single product detail page
     * Uses constant path: /product/:productSlug or /product (for first product)
     */
    export interface ProductItem extends Base<typeof DoppeViewerPageType.ProductItem> {
        /**
         * Product slug from URL (e.g., 'my-cool-product')
         * When undefined or empty string, the page will load the first available product.
         */
        productSlug: string;
    }
    
    // ========================================================================
    // Blog Posts (Wix Blog)
    // ========================================================================
    
    /**
     * Blog posts list page (shows all blog posts)
     * Uses constant path: /blog
     */
    export interface BlogPostsList extends Base<typeof DoppeViewerPageType.BlogPostsList> {
        // No pageId - uses constant path
    }
    
    /**
     * Single blog post detail page
     * Uses constant path: /post/:postSlug or /post (for first blog post)
     */
    export interface BlogPostItem extends Base<typeof DoppeViewerPageType.BlogPostItem> {
        /**
         * Blog post slug from URL (e.g., 'my-awesome-post')
         * When undefined or empty string, the page will load the first available blog post.
         */
        postSlug?: string;
    }
    
    // ========================================================================
    // Events (Wix Events)
    // ========================================================================
    
    /**
     * Events list page (shows all events)
     * Uses constant path: /events
     */
    export interface EventsList extends Base<typeof DoppeViewerPageType.EventsList> {
        // No pageId - uses constant path
    }
    
    /**
     * Single event detail page
     * Uses constant path: /event/:eventSlug or /event (for first event)
     */
    export interface EventItem extends Base<typeof DoppeViewerPageType.EventItem> {
        /**
         * Event slug from URL (e.g., 'conference-2024')
         * When undefined or empty string, the page will load the first available event.
         */
        eventSlug?: string;
    }
    
    // ========================================================================
    // Services (Wix Bookings)
    // ========================================================================
    
    /**
     * Services list page (shows all bookable services)
     * Uses constant path: /services
     */
    export interface ServicesList extends Base<typeof DoppeViewerPageType.ServicesList> {
        // No pageId - uses constant path
    }
    
    /**
     * Single service detail page
     * Uses constant path: /service/:serviceSlug or /service (for first service)
     */
    export interface ServiceItem extends Base<typeof DoppeViewerPageType.ServiceItem> {
        /**
         * Service slug from URL (e.g., 'consultation')
         * When undefined or empty string, the page will load the first available service.
         */
        serviceSlug: string;
    }
}

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Type guard to check if page info is home page
 */
export function doppeViewerPageInfoIsHome(pageInfo: DoppeViewerPageInfo): pageInfo is DoppeViewerPageInfo.Home {
    return pageInfo.pageType === DoppeViewerPageType.Home;
}

/**
 * Type guard to check if page info is content page
 */
export function doppeViewerPageInfoIsContentPage(pageInfo: DoppeViewerPageInfo): pageInfo is DoppeViewerPageInfo.ContentPage {
    return pageInfo.pageType === DoppeViewerPageType.ContentPage;
}

/**
 * Type guard to check if page info is products list
 */
export function doppeViewerPageInfoIsProductsList(pageInfo: DoppeViewerPageInfo): pageInfo is DoppeViewerPageInfo.ProductsList {
    return pageInfo.pageType === DoppeViewerPageType.ProductsList;
}

/**
 * Type guard to check if page info is product item
 */
export function doppeViewerPageInfoIsProductItem(pageInfo: DoppeViewerPageInfo): pageInfo is DoppeViewerPageInfo.ProductItem {
    return pageInfo.pageType === DoppeViewerPageType.ProductItem;
}

/**
 * Type guard to check if page info is blog posts list
 */
export function doppeViewerPageInfoIsBlogPostsList(pageInfo: DoppeViewerPageInfo): pageInfo is DoppeViewerPageInfo.BlogPostsList {
    return pageInfo.pageType === DoppeViewerPageType.BlogPostsList;
}

/**
 * Type guard to check if page info is blog post item
 */
export function doppeViewerPageInfoIsBlogPostItem(pageInfo: DoppeViewerPageInfo): pageInfo is DoppeViewerPageInfo.BlogPostItem {
    return pageInfo.pageType === DoppeViewerPageType.BlogPostItem;
}

/**
 * Type guard to check if page info is events list
 */
export function doppeViewerPageInfoIsEventsList(pageInfo: DoppeViewerPageInfo): pageInfo is DoppeViewerPageInfo.EventsList {
    return pageInfo.pageType === DoppeViewerPageType.EventsList;
}

/**
 * Type guard to check if page info is event item
 */
export function doppeViewerPageInfoIsEventItem(pageInfo: DoppeViewerPageInfo): pageInfo is DoppeViewerPageInfo.EventItem {
    return pageInfo.pageType === DoppeViewerPageType.EventItem;
}

/**
 * Type guard to check if page info is services list
 */
export function doppeViewerPageInfoIsServicesList(pageInfo: DoppeViewerPageInfo): pageInfo is DoppeViewerPageInfo.ServicesList {
    return pageInfo.pageType === DoppeViewerPageType.ServicesList;
}

/**
 * Type guard to check if page info is service item
 */
export function doppeViewerPageInfoIsServiceItem(pageInfo: DoppeViewerPageInfo): pageInfo is DoppeViewerPageInfo.ServiceItem {
    return pageInfo.pageType === DoppeViewerPageType.ServiceItem;
}

/**
 * Type guard to check if page info is any list page (not item detail)
 */
export function doppeViewerPageInfoIsListPage(pageInfo: DoppeViewerPageInfo): pageInfo is 
    | DoppeViewerPageInfo.ProductsList 
    | DoppeViewerPageInfo.BlogPostsList 
    | DoppeViewerPageInfo.EventsList 
    | DoppeViewerPageInfo.ServicesList {
    return pageInfo.pageType === DoppeViewerPageType.ProductsList
        || pageInfo.pageType === DoppeViewerPageType.BlogPostsList
        || pageInfo.pageType === DoppeViewerPageType.EventsList
        || pageInfo.pageType === DoppeViewerPageType.ServicesList;
}

/**
 * Type guard to check if page info is any item detail page (not list)
 */
export function doppeViewerPageInfoIsItemPage(pageInfo: DoppeViewerPageInfo): pageInfo is 
    | DoppeViewerPageInfo.ProductItem 
    | DoppeViewerPageInfo.BlogPostItem 
    | DoppeViewerPageInfo.EventItem 
    | DoppeViewerPageInfo.ServiceItem {
    return pageInfo.pageType === DoppeViewerPageType.ProductItem
        || pageInfo.pageType === DoppeViewerPageType.BlogPostItem
        || pageInfo.pageType === DoppeViewerPageType.EventItem
        || pageInfo.pageType === DoppeViewerPageType.ServiceItem;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get the item slug from page info (if it's an item page)
 * Returns null for non-item pages or when no slug is provided (for first item pages)
 */
export function doppeViewerPageInfoGetItemSlug(pageInfo: DoppeViewerPageInfo): string | null {
    if (doppeViewerPageInfoIsProductItem(pageInfo)) {
        return pageInfo.productSlug ?? null;
    }
    
    if (doppeViewerPageInfoIsBlogPostItem(pageInfo)) {
        return pageInfo.postSlug ?? null;
    }
    
    if (doppeViewerPageInfoIsEventItem(pageInfo)) {
        return pageInfo.eventSlug ?? null;
    }
    
    if (doppeViewerPageInfoIsServiceItem(pageInfo)) {
        return pageInfo.serviceSlug ?? null;
    }
    
    return null;
}

/**
 * Create home page info
 */
export function doppeViewerPageInfoCreateHome(): DoppeViewerPageInfo.Home {
    return {
        pageType: DoppeViewerPageType.Home
    };
}

/**
 * Create content page info
 */
export function doppeViewerPageInfoCreateContentPage(
    slug: string
): DoppeViewerPageInfo.ContentPage {
    return {
        pageType: DoppeViewerPageType.ContentPage,
        slug: slug
    };
}

/**
 * Create products list page info
 */
export function doppeViewerPageInfoCreateProductsList(): DoppeViewerPageInfo.ProductsList {
    return {
        pageType: DoppeViewerPageType.ProductsList
    };
}

/**
 * Create product item page info
 * @param productSlug - Product slug. If not provided, the page will load the first available product.
 */
export function doppeViewerPageInfoCreateProductItem(
    productSlug?: string
): DoppeViewerPageInfo.ProductItem {
    return {
        pageType: DoppeViewerPageType.ProductItem,
        productSlug: productSlug ?? ''
    };
}

/**
 * Create blog posts list page info
 */
export function doppeViewerPageInfoCreateBlogPostsList(): DoppeViewerPageInfo.BlogPostsList {
    return {
        pageType: DoppeViewerPageType.BlogPostsList
    };
}

/**
 * Create blog post item page info
 * @param postSlug - Blog post slug. If not provided, the page will load the first available blog post.
 */
export function doppeViewerPageInfoCreateBlogPostItem(
    postSlug?: string
): DoppeViewerPageInfo.BlogPostItem {
    return {
        pageType: DoppeViewerPageType.BlogPostItem,
        postSlug: postSlug ?? ''
    };
}

/**
 * Create events list page info
 */
export function doppeViewerPageInfoCreateEventsList(): DoppeViewerPageInfo.EventsList {
    return {
        pageType: DoppeViewerPageType.EventsList
    };
}

/**
 * Create event item page info
 * @param eventSlug - Event slug. If not provided, the page will load the first available event.
 */
export function doppeViewerPageInfoCreateEventItem(
    eventSlug?: string
): DoppeViewerPageInfo.EventItem {
    return {
        pageType: DoppeViewerPageType.EventItem,
        eventSlug: eventSlug ?? ''
    };
}

/**
 * Create services list page info
 */
export function doppeViewerPageInfoCreateServicesList(): DoppeViewerPageInfo.ServicesList {
    return {
        pageType: DoppeViewerPageType.ServicesList
    };
}

/**
 * Create service item page info
 * @param serviceSlug - Service slug. If not provided, the page will load the first available service.
 */
export function doppeViewerPageInfoCreateServiceItem(
    serviceSlug?: string
): DoppeViewerPageInfo.ServiceItem {
    return {
        pageType: DoppeViewerPageType.ServiceItem,
        serviceSlug: serviceSlug ?? ''
    };
}


export function doppeViewerPageInfoResolvePageId(pageInfo: DoppeViewerPageInfo, bymoPageProps: BymoPageProps<any>): string | null {

    if (doppeViewerPageInfoIsHome(pageInfo)) {
        return DoppeDtoInnerPageKnownIds.Home;
    }

    if (doppeViewerPageInfoIsContentPage(pageInfo)) {
        if (bymoPageProps) {
            const contentPage = bymoPageProps.innerPages.find((p: DoppeDtoInnerPage) => p.slug === pageInfo.slug);
            if (contentPage) {
                return contentPage.id;
            }
        }

        return null;
    }

    if (doppeViewerPageInfoIsProductsList(pageInfo)) {
        return DoppeDtoInnerPageKnownIds.Products;
    }

    if (doppeViewerPageInfoIsBlogPostsList(pageInfo)) {
        return DoppeDtoInnerPageKnownIds.BlogPosts;
    }

    if (doppeViewerPageInfoIsEventsList(pageInfo)) {
        return DoppeDtoInnerPageKnownIds.Events;
    }

    if (doppeViewerPageInfoIsServicesList(pageInfo)) {
        return DoppeDtoInnerPageKnownIds.Services;
    }

    if (doppeViewerPageInfoIsProductItem(pageInfo)) {
        return DoppeDtoInnerPageKnownIds.Product;
    }

    if (doppeViewerPageInfoIsBlogPostItem(pageInfo)) {
        return DoppeDtoInnerPageKnownIds.BlogPost;
    }

    if (doppeViewerPageInfoIsEventItem(pageInfo)) {
        return DoppeDtoInnerPageKnownIds.Event;
    }

    if (doppeViewerPageInfoIsServiceItem(pageInfo)) {
        return DoppeDtoInnerPageKnownIds.Service;
    }

    return null;
}
